import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { BedIllustration } from '../../shared/illustrations/BedIllustration.js';
import { BrandMark } from '../../shared/illustrations/BrandMark.js';
import { GuestAvatar } from '../../shared/illustrations/GuestAvatar.js';
import { PageBackdrop } from '../../shared/illustrations/PageBackdrop.js';
import { useLanguage } from '../../shared/i18n/useLanguage.js';
import { useTheme } from '../../shared/theme/useTheme.js';
import { Button, Card } from '../../shared/ui/index.js';

/**
 * The public home page — what an anonymous visitor sees at `/` before
 * signing in. Reuses the app's own chrome classes (`.app-shell`,
 * `.app-header`, `.app-main`) and illustrations rather than inventing a
 * parallel visual language, so it reads as the same product, not a
 * marketing microsite bolted on the side.
 */
export function LandingPage(): ReactElement {
  const { language, t, setLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="app-shell">
      <PageBackdrop />

      <header className="app-header">
        <Link to="/" className="app-brand">
          <BrandMark />
          {t.nav.brand}
        </Link>

        {/* No nav links on this page to absorb the flexible space the way
            `.app-nav` does in `AppLayout`, so this spacer does that job
            instead, keeping the toggles and CTAs pinned to the right. */}
        <div className="app-header__spacer" />

        <div className="lang-toggle" role="group" aria-label={t.nav.language}>
          <button
            type="button"
            aria-pressed={language === 'en'}
            aria-label={t.nav.languageToggle('en')}
            onClick={() => setLanguage('en')}
          >
            EN
          </button>
          <button
            type="button"
            aria-pressed={language === 'fr'}
            aria-label={t.nav.languageToggle('fr')}
            onClick={() => setLanguage('fr')}
          >
            FR
          </button>
        </div>
        <button
          type="button"
          className="theme-toggle"
          aria-label={t.nav.themeToggle(theme === 'dark' ? 'light' : 'dark')}
          onClick={toggleTheme}
        >
          <span aria-hidden="true">{theme === 'dark' ? '🌙' : '☀️'}</span>
        </button>

        <div className="row">
          <Link to="/login">
            <Button variant="secondary">{t.auth.signInTitle}</Button>
          </Link>
          <Link to="/register">
            <Button>{t.landing.getStarted}</Button>
          </Link>
        </div>
      </header>

      <main className="app-main">
        <section className="landing-section landing-hero">
          <div className="landing-hero__text">
            <h1>{t.landing.heroTitle}</h1>
            <p className="page-header__subtitle">{t.auth.tagline}</p>
            <p>{t.landing.heroPitch}</p>
            <div className="row">
              <Link to="/register">
                <Button>{t.landing.getStarted}</Button>
              </Link>
              <Link to="/login">
                <Button variant="secondary">{t.auth.signInTitle}</Button>
              </Link>
            </div>
          </div>
          <div className="landing-hero__avatars" aria-hidden="true">
            <GuestAvatar guestId="landing-demo-woman" name="Guest" gender="woman" size="xl" />
            <GuestAvatar guestId="landing-demo-man" name="Guest" gender="man" size="xl" />
            <GuestAvatar guestId="landing-demo-other" name="Guest" gender="other" size="xl" />
          </div>
        </section>

        <section className="landing-section">
          <h2>{t.landing.featuresTitle}</h2>
          <div className="landing-features">
            <Card>
              <div className="stack stack--tight">
                <h3>{t.landing.feature1Title}</h3>
                <p>{t.landing.feature1Description}</p>
                <div className="row">
                  <BedIllustration type="single" option="standard" size="lg" />
                  <BedIllustration type="double" option="bunk_top" size="lg" />
                </div>
              </div>
            </Card>

            <Card>
              <div className="stack stack--tight">
                <h3>{t.landing.feature2Title}</h3>
                <p>{t.landing.feature2Description}</p>
              </div>
            </Card>

            <Card>
              <div className="stack stack--tight">
                <h3>{t.landing.feature3Title}</h3>
                <p>{t.landing.feature3Description}</p>
              </div>
            </Card>

            <Card>
              <div className="stack stack--tight">
                <h3>{t.landing.feature4Title}</h3>
                <p>{t.landing.feature4Description}</p>
              </div>
            </Card>
          </div>
        </section>

        <section className="landing-section">
          <h2>{t.landing.stepsTitle}</h2>
          <ol className="landing-steps">
            <li>
              <h3>{t.landing.step1Title}</h3>
              <p>{t.landing.step1Description}</p>
            </li>
            <li>
              <h3>{t.landing.step2Title}</h3>
              <p>{t.landing.step2Description}</p>
            </li>
            <li>
              <h3>{t.landing.step3Title}</h3>
              <p>{t.landing.step3Description}</p>
            </li>
            <li>
              <h3>{t.landing.step4Title}</h3>
              <p>{t.landing.step4Description}</p>
            </li>
            <li>
              <h3>{t.landing.step5Title}</h3>
              <p>{t.landing.step5Description}</p>
            </li>
          </ol>
        </section>

        <section className="landing-section landing-cta">
          <h2>{t.landing.ctaTitle}</h2>
          <div className="row" style={{ justifyContent: 'center' }}>
            <Link to="/register">
              <Button>{t.landing.getStarted}</Button>
            </Link>
            <Link to="/login">
              <Button variant="secondary">{t.auth.signInTitle}</Button>
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
